import { z } from "zod/v4"
import type { DocumentSnapshotLike } from "../../firestore"
import { DocumentStatus } from "../../common"
import { MaintenanceScheduleEntryId, MaintenanceScheduleEntryIdSchema, UserIdSchema } from "../../id"
import { Period, PeriodState } from "../common"
import { QuillDeltaServerSchema } from "../quill-delta"
import { CreatedAt, UpdatedAt } from "../system"

/**
 * メンテナンス対象エリアのターゲット
 * - space-live: スペース・ライブ（ライブを含む）
 * - live: ライブのみ
 * - shop: ショップのみ
 */
export const MaintenanceScheduleAreaTarget = z.enum(["space-live", "live", "shop"])
  .describe("部分メンテナンスの対象エリア")
export type MaintenanceScheduleAreaTarget = z.infer<typeof MaintenanceScheduleAreaTarget>

/**
 * メンテナンス対象エリア
 * - all: 全体（スペース・ライブ・ショップすべて）
 * - partial: 部分（targets で指定したエリアのみ）
 */
export const MaintenanceScheduleArea = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("all").describe("全体メンテナンス"),
  }),
  z.object({
    kind: z.literal("partial").describe("部分メンテナンス"),
    targets: z.array(MaintenanceScheduleAreaTarget).min(1).describe("部分メンテナンスの対象エリア（複数選択可）"),
  }),
]).describe("メンテナンス対象エリア")
export type MaintenanceScheduleArea = z.infer<typeof MaintenanceScheduleArea>

/**
 * メンテナンス段階
 * - maintenance: メンテ中（開始時刻前はメンテ前、開始時刻後はメンテ中として解釈）
 * - internal-check: 内部確認中（管理者のみアクセス可能）
 * - released: 公開（メンテ明け）
 */
export const MaintenanceScheduleStage = z.enum(["maintenance", "internal-check", "released"])
  .describe("メンテナンス段階。maintenance=メンテ中, internal-check=内部確認中, released=公開")
export type MaintenanceScheduleStage = z.infer<typeof MaintenanceScheduleStage>

/**
 * メンテナンス告知の設定（メンテ前・メンテ中を含む任意の期間で表示可能）
 * クライアントは次のannouncementのstart_atが来たら、前のannouncementを自動終了として扱う必要があります。
 */
export const MaintenanceScheduleAnnouncement = z.object({
  start_at: z.number().describe("告知を表示開始する日時 (UNIX Timestamp millis)"),
  end_at: z.number().optional().describe("告知を表示終了する日時 (UNIX Timestamp millis)。省略時は次のannouncementのstart_atまで、または無期限"),
  display_content: z.any().describe("表示内容（QuillのDelta形式）"),
}).describe("メンテナンス告知の設定")
export type MaintenanceScheduleAnnouncement = z.infer<typeof MaintenanceScheduleAnnouncement>

/**
 * メンテナンススケジュールエントリ（配列内の各要素）
 */
export const MaintenanceScheduleEntry = z.object({
  id: MaintenanceScheduleEntryIdSchema.describe("エントリのID（ランダム文字列）"),
  title: z.string().describe("メンテナンスのタイトル（管理用）"),
  area: MaintenanceScheduleArea.describe("メンテナンス対象エリア"),
  period: Period.describe("メンテナンス期間"),
  period_state: PeriodState.describe("メンテナンス期間の状態。activeフィールドは人間が手動で設定し、trueの場合はメンテナンス中として扱う。next_change_atは参考情報として自動計算。"),
  stage: MaintenanceScheduleStage.describe("現在のメンテナンス段階"),
  maintenance_display_content: z.any().describe("メンテナンス中の表示内容（QuillのDelta形式）"),
  announcements: z.array(MaintenanceScheduleAnnouncement).optional().describe("メンテナンス告知の設定（メンテ前・メンテ中を含む任意の期間で表示可能、複数登録可能）"),
  status: DocumentStatus.describe("メンテナンススケジュールの状態。public=有効、hidden=無効"),
  created_at: CreatedAt,
  updated_at: UpdatedAt,
}).describe("メンテナンススケジュールエントリ")

export type MaintenanceScheduleEntry = z.infer<typeof MaintenanceScheduleEntry>

/**
 * 内部確認ユーザー
 * internal-check 段階でアクセス可能なユーザー情報
 */
export const MaintenanceInternalUser = z.object({
  user_id: z.string().describe("ユーザーID"),
  note: z.string().max(40).describe("備考（このユーザーが誰か、追加理由など。最大40文字）"),
  created_at: CreatedAt,
  updated_at: UpdatedAt,
}).describe("内部確認ユーザー")

export type MaintenanceInternalUser = z.infer<typeof MaintenanceInternalUser>

/**
 * メンテナンスドキュメント（/app-configs/maintenance に保存される単一ドキュメント）
 * 全てのメンテナンススケジュールを配列で管理します。
 * 最大10件程度を想定（ドキュメントサイズ上限1MBを考慮）
 */
export const AppConfigMaintenanceDocumentBody = z.object({
  schedules: z.array(MaintenanceScheduleEntry).describe("メンテナンススケジュールの配列（最大10件程度）"),
  internal_users: z.array(MaintenanceInternalUser).optional().describe("内部確認ユーザーの配列（internal-check段階でアクセス可能なユーザー）"),
}).describe("メンテナンス管理ドキュメント。/app-configs/maintenance に保存される単一ドキュメントです。")

export type AppConfigMaintenanceDocumentBody = z.infer<typeof AppConfigMaintenanceDocumentBody>

export class MaintenanceDocuments {
  static readonly DOCUMENT_ID = "maintenance"
  static readonly MAX_SCHEDULES = 10

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): AppConfigMaintenanceDocumentBody | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }
    return {
      schedules: data["schedules"] ?? [],
      internal_users: data["internal_users"],
    }
  }

  static toDocumentData(maintenance: AppConfigMaintenanceDocumentBody): object {
    return {
      schedules: maintenance.schedules,
      internal_users: maintenance.internal_users,
    }
  }

  /**
   * 新しいエントリIDを生成
   */
  static generateEntryId(): MaintenanceScheduleEntryId {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
    let result = ''
    for (let i = 0; i < 20; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length))
    }
    return MaintenanceScheduleEntryId(result)
  }

  /**
   * 指定されたIDのエントリを取得
   */
  static getEntryById(doc: AppConfigMaintenanceDocumentBody, id: MaintenanceScheduleEntryId): MaintenanceScheduleEntry | null {
    return doc.schedules.find(s => s.id === id) ?? null
  }

  /**
   * 指定されたエリアがメンテナンス対象かどうかを判定
   * @param area メンテナンス対象エリア設定
   * @param target 判定対象のエリア
   */
  static isAreaAffected(area: MaintenanceScheduleArea, target: MaintenanceScheduleAreaTarget): boolean {
    if (area.kind === "all") {
      return true
    }
    // space-live が対象の場合、live も影響を受ける
    if (target === "live" && area.targets.includes("space-live")) {
      return true
    }
    return area.targets.includes(target)
  }

  /**
   * メンテナンス中かどうかを判定
   * period_state.active が true かつ status が public かつ stage が released でない場合にメンテナンス中と判定
   * @param entry メンテナンススケジュールエントリ
   */
  static isUnderMaintenance(
    entry: MaintenanceScheduleEntry,
  ): boolean {
    if (entry.status !== "public") {
      return false
    }
    if (entry.stage === "released") {
      return false
    }
    return entry.period_state.active
  }

  /**
   * 内部確認中かどうかを判定
   * @param entry メンテナンススケジュールエントリ
   * @param currentTime 現在時刻 (UNIX Timestamp millis)
   */
  static isInternalCheck(
    entry: MaintenanceScheduleEntry,
    currentTime: number = Date.now(),
  ): boolean {
    if (entry.status !== "public") {
      return false
    }
    if (entry.stage !== "internal-check") {
      return false
    }
    return entry.period.start_at <= currentTime
  }

  /**
   * 指定されたエリアにアクセス可能かどうかを判定
   * @param entry メンテナンススケジュールエントリ
   * @param target 判定対象のエリア
   * @param isAdmin 管理者かどうか
   * @param currentTime 現在時刻 (UNIX Timestamp millis)
   */
  static canAccess(
    entry: MaintenanceScheduleEntry,
    target: MaintenanceScheduleAreaTarget,
    isAdmin: boolean,
    currentTime: number = Date.now(),
  ): boolean {
    // このエリアがメンテナンス対象でない場合はアクセス可能
    if (!MaintenanceDocuments.isAreaAffected(entry.area, target)) {
      return true
    }
    // メンテ明けの場合はアクセス可能
    if (entry.stage === "released") {
      return true
    }
    // 開始時刻前はアクセス可能
    if (currentTime < entry.period.start_at) {
      return true
    }
    // 内部確認中で管理者の場合はアクセス可能
    if (entry.stage === "internal-check" && isAdmin) {
      return true
    }
    // それ以外はアクセス不可
    return false
  }

  /**
   * アクティブなメンテナンススケジュールを取得（status=public かつ stage!=released）
   */
  static getActiveSchedules(doc: AppConfigMaintenanceDocumentBody): MaintenanceScheduleEntry[] {
    return doc.schedules.filter(s => s.status === "public" && s.stage !== "released")
  }

  /**
   * メンテナンス中のスケジュールがあるかどうかを判定
   */
  static hasActiveMaintenanceSchedule(doc: AppConfigMaintenanceDocumentBody): boolean {
    return doc.schedules.some(s => MaintenanceDocuments.isUnderMaintenance(s))
  }

  /**
   * 2つの期間が重複しているかどうかを判定
   * @param periodA 期間A
   * @param periodB 期間B
   * @returns 重複している場合はtrue
   */
  static isPeriodsOverlapping(
    periodA: { start_at: number; end_at: number | null },
    periodB: { start_at: number; end_at: number | null }
  ): boolean {
    // 期間が重複する条件: A.start < B.end AND B.start < A.end
    // end_at が null の場合は無期限なので、常に重複する可能性がある
    const aEndsBeforeBStarts = periodA.end_at !== null && periodA.end_at <= periodB.start_at
    const bEndsBeforeAStarts = periodB.end_at !== null && periodB.end_at <= periodA.start_at
    
    // どちらかの期間が他方の開始前に終了していれば重複しない
    return !aEndsBeforeBStarts && !bEndsBeforeAStarts
  }

  /**
   * 指定された期間が既存のスケジュールと重複しているかどうかを判定
   * @param doc メンテナンスドキュメント
   * @param period チェックする期間
   * @param excludeId 除外するエントリID（更新時に自分自身を除外するため）
   * @returns 重複しているスケジュールがあればそのエントリ、なければnull
   */
  static findOverlappingSchedule(
    doc: AppConfigMaintenanceDocumentBody,
    period: { start_at: number; end_at: number | null },
    excludeId?: MaintenanceScheduleEntryId
  ): MaintenanceScheduleEntry | null {
    for (const schedule of doc.schedules) {
      // 自分自身は除外
      if (excludeId && schedule.id === excludeId) {
        continue
      }
      // released（メンテ明け）のスケジュールは除外
      if (schedule.stage === "released") {
        continue
      }
      // 期間が重複しているかチェック
      if (MaintenanceDocuments.isPeriodsOverlapping(period, schedule.period)) {
        return schedule
      }
    }
    return null
  }

  /**
   * 指定されたユーザーIDが内部確認ユーザーに含まれているか確認
   * @param doc メンテナンスドキュメント
   * @param userId ユーザーID
   */
  static isInternalUser(doc: AppConfigMaintenanceDocumentBody, userId: string): boolean {
    return doc.internal_users?.some(u => u.user_id === userId) ?? false
  }

  /**
   * 指定されたユーザーIDの内部確認ユーザーを取得
   * @param doc メンテナンスドキュメント
   * @param userId ユーザーID
   */
  static getInternalUser(doc: AppConfigMaintenanceDocumentBody, userId: string): MaintenanceInternalUser | null {
    return doc.internal_users?.find(u => u.user_id === userId) ?? null
  }
}

/**
 * メンテナンススケジュール作成リクエスト
 */
export const CreateMaintenanceScheduleRequest = z.object({
  title: z.string(),
  area: MaintenanceScheduleArea,
  period: z.object({
    startAt: z.number().describe("メンテナンス開始日時 (UNIX Timestamp millis)"),
    endAt: z.number().describe("メンテナンス終了予定日時 (UNIX Timestamp millis)"),
  }),
  maintenanceDisplayContent: QuillDeltaServerSchema,
  announcements: z.array(z.object({
    startAt: z.number().describe("告知を表示開始する日時 (UNIX Timestamp millis)"),
    endAt: z.number().optional().describe("告知を表示終了する日時 (UNIX Timestamp millis)。省略時は次のannouncementのstartAtまで、または無期限"),
    displayContent: QuillDeltaServerSchema,
  })).optional(),
  status: DocumentStatus.default("hidden"),
}).refine(
  (data) => data.period.startAt < data.period.endAt,
  {
    message: "開始日時は終了日時より前である必要があります",
    path: ["period", "endAt"],
  }
)

export type CreateMaintenanceScheduleRequest = z.infer<typeof CreateMaintenanceScheduleRequest>

/**
 * メンテナンススケジュール更新リクエスト
 */
export const UpdateMaintenanceScheduleRequest = z.object({
  title: z.string().optional(),
  area: MaintenanceScheduleArea.optional(),
  period: z.object({
    startAt: z.number().describe("メンテナンス開始日時 (UNIX Timestamp millis)"),
    endAt: z.number().describe("メンテナンス終了予定日時 (UNIX Timestamp millis)"),
  }).optional(),
  stage: MaintenanceScheduleStage.optional(),
  maintenanceDisplayContent: QuillDeltaServerSchema.optional(),
  announcements: z.array(z.object({
    startAt: z.number().describe("告知を表示開始する日時 (UNIX Timestamp millis)"),
    endAt: z.number().optional().describe("告知を表示終了する日時 (UNIX Timestamp millis)。省略時は次のannouncementのstartAtまで、または無期限"),
    displayContent: QuillDeltaServerSchema,
  })).optional(),
  status: DocumentStatus.optional(),
  periodStateActive: z.boolean().optional().describe("メンテナンス期間の有効状態。trueの場合はメンテナンス中として扱う。start_atとは独立して人間が手動で設定する。"),
}).refine(
  (data) => {
    if (data.period) {
      return data.period.startAt < data.period.endAt
    }
    return true
  },
  {
    message: "開始日時は終了日時より前である必要があります",
    path: ["period", "endAt"],
  }
)

export type UpdateMaintenanceScheduleRequest = z.infer<typeof UpdateMaintenanceScheduleRequest>

/**
 * 内部確認ユーザー作成リクエスト
 */
export const CreateMaintenanceInternalUserRequest = z.object({
  userId: UserIdSchema,
  note: z.string().min(1, "noteは必須です").max(40, "noteは40文字以内で入力してください"),
})

export type CreateMaintenanceInternalUserRequest = z.infer<typeof CreateMaintenanceInternalUserRequest>

/**
 * 内部確認ユーザー更新リクエスト
 */
export const UpdateMaintenanceInternalUserRequest = z.object({
  note: z.string().min(1, "noteは必須です").max(40, "noteは40文字以内で入力してください"),
})

export type UpdateMaintenanceInternalUserRequest = z.infer<typeof UpdateMaintenanceInternalUserRequest>
